import { ReactNode, MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import { colors, s16, s8, shadowS } from '@ardoq/design-tokens';
import { text1Bold } from '@ardoq/typography';
import { focusableCSS } from '@ardoq/style-helpers';
import { ICON_SELECTOR, SpinnerIcon } from '@ardoq/icons';

const BUTTON_WIDTH = 245;
const BUTTON_HEIGHT = 72;

const LoadingIconWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: calc(50% - 12px);
`;
const ButtonStyle = styled.button<{
  $isLoading: boolean;
  $isDisabled: boolean;
}>`
  ${text1Bold};
  ${focusableCSS};
  width: ${BUTTON_WIDTH}px;
  border: 1px solid ${colors.borderAction};
  border-radius: ${s8};
  &:hover {
    border-color: ${colors.borderActionHover};
  }
  &:active {
    border-color: ${colors.borderActionActive};
    background-color: ${colors.surfaceActionTertiaryPressed};
  }
  height: ${BUTTON_HEIGHT}px;
  padding: ${s16};
  background: ${colors.surfaceDefault};
  box-shadow: ${shadowS};
  color: ${colors.textModerate};
  display: flex;
  position: relative;
  align-items: center;
  cursor: pointer;
  ${ICON_SELECTOR} {
    color: ${colors.icon};
  }
  ${ICON_SELECTOR}:first-child {
    margin-right: ${s16};
  }
  ${ICON_SELECTOR} ~ ${ICON_SELECTOR} {
    /* Selecting second icon using sibling selector */
    margin-left: auto;
  }
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      cursor: default;
      color: ${colors.textDisabled};
      pointer-events: none;
      border-color: ${colors.borderActionDisabled};
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
      }
    `}
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${colors.textDisabled};
      border-color: ${colors.borderActionDisabled};
      background-color: ${colors.surfaceDisabled};
      box-shadow: none;
      pointer-events: none;
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
      }
    `}
`;

type BigButtonProps = {
  isLoading?: boolean;
  isDisabled?: boolean;
  children: ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement>;
  clickId?: string;
  dataTestId?: string;
  className?: string;
};

const BigButton = ({
  children,
  isLoading,
  isDisabled,
  onClick,
  clickId,
  dataTestId,
  className,
}: BigButtonProps) => {
  return (
    <ButtonStyle
      className={className}
      type="button"
      onClick={onClick}
      $isLoading={Boolean(isLoading)}
      $isDisabled={Boolean(isDisabled)}
      data-click-id={clickId}
      data-test-id={dataTestId}
      disabled={isDisabled}
    >
      {children}
      {isLoading && (
        <LoadingIconWrapper>
          <SpinnerIcon />
        </LoadingIconWrapper>
      )}
    </ButtonStyle>
  );
};

export { BigButton };
